import React from "react";
import { CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY, EarningRuleRewardType, EarningRuleType } from "config/CustomEnums";

import LimitSection, { LevelUpgradePerHeadPeriodicLimitSection } from "components/earning/LimitSection.js";
import QRCodeScanningSection from "components/earning/QRCodeScanningSection.js";
import LevelUpgradeSection from "components/earning/LevelUpgradeSection.js";
import InputFieldControl from "components/base/InputFieldControl.js";
import { StoreMultipleSelector } from "components/earning/TargetTypeCustomerSelector.js";
import ReactHookFormSwitch from "components/reactHookFormComponents/ReactHookFormSwitch.js";
import ReactHookFormTitleWithRadio from "components/reactHookFormComponents/ReactHookFormTitleWithRadio.js";
import RewardLogicSelector from "components/earning/RewardLogicSelector.js";
import RewardAmountInputArea from "components/earning/RewardAmountInputArea.js";
import PurchaseEarningRuleRewardLimitInputs from "components/earning/PurchaseEarningRuleRewardLimitInputs.js";
import ReactHookFormTextInput from "components/reactHookFormComponents/ReactHookFormTextInput.js";
import SkuTypeSelector from "components/earning/SkuTypeSelector.js";
import GeneralPurchasePerMin from "components/earning/GeneralPurchasePerMin.js";
import { tierTypeConfig } from "config/TierTypeConfig";


const getRewardQuantityInput = (fieldPrefix = "") => ({
  key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.reward.sectionContent.reward_quantity_key,
  complexComponent: ({ formContext: { watch } }) => {
    const linkedStampCampaign = watch(`${fieldPrefix}linkedStampCampaign`)
    const rewardType = watch(`${fieldPrefix}rewardType`)
    return (
      <InputFieldControl
        name={`${fieldPrefix}quantity`}
        title="Reward quantity"
        useDefaultFieldName
        type="number"
        unit={linkedStampCampaign
          ? "stamp per action"
          : rewardType === EarningRuleRewardType.points
            ? "number of points"
            : "coupons per number"
        }
        className="coupon-campaign-section-input"
      />
    )
  }
})

const getRewardTpeInput = (fieldPrefix = "") => (
  {
    key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.reward.sectionContent.reward_tpe_key,
    component: InputFieldControl,
    componentProps: {
      name: `${fieldPrefix}tpeQuantity`,
      useDefaultFieldName: true,
      type: "number",
      unit: "number of progression points",
      className: "coupon-campaign-section-input",
      errorId: `${fieldPrefix}pointReward`,
    },
  }
)
const getExecutionPrioritySection = (fieldPrefix = "") => ({
  sectionKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.executionPriority.sectionKey,
  sectionTitle: "EXECUTION PRIORITY FOR GLOBAL LIMIT",
  sectionContent: [
    {
      key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.executionPriority.sectionContent.execution_priority_input_key,
      component: ReactHookFormTextInput,
      componentProps: {
        customClass: "width-160px",
        type: "number",
        valueFieldName: `${fieldPrefix}executionPriority`,
        title: 'Execution priority',
        tips: "Set the priority for executing this earning rule relative to the other rules in the system. If a global maximum reward limit is set, only the earning rules with a higher priority will be able to issue rewards.",
        shortDescription: "Minimum value is 1. The smaller value, the higher priority."
      }
    }
  ],
})

const getRewardSection = (fieldPrefix = "") => ({
  sectionKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.reward.sectionKey,
  sectionTitle: "REWARD",
  sectionContent: [
    getRewardQuantityInput(fieldPrefix),
    getRewardTpeInput(fieldPrefix)
  ]
})


export const LimitsSplitLine = () => (
  <div className="earning-rule-limit-split-line" />
)

export const getEarningRuleCreationConfig = (fieldPrefix = "") => ({
  isConfigByKey: true,
  getSectionKey: ({ formContext: { watch } }) => {
    return watch(`${fieldPrefix}type`) || ""
  },
  getDisplayComponentKeys: ({ formContext: { watch } }) => {
    const earningRuleType = watch(`${fieldPrefix}type`)
    const lineItemRequirement = watch(`${fieldPrefix}lineItemRequirement`)
    const haveEligibleStores = watch(`${fieldPrefix}haveEligibleStores`);
    const haveLimits = watch(`${fieldPrefix}haveLimitsField`);
    const requirePromotionCode = watch(`${fieldPrefix}requirePromotionCode`);
    const rewardType = watch(`${fieldPrefix}rewardType`)
    const referralType = watch(`${fieldPrefix}referralType`)
    const linkedStampCampaign = watch(`${fieldPrefix}linkedStampCampaign`)

    const getRewardSectionDisplayComponents = () => {
      if (linkedStampCampaign && [
        EarningRuleType.generalPurchase,
        EarningRuleType.gpsCheckIn,
        EarningRuleType.qrCodeScanning,
        EarningRuleType.memberReferral,
      ].includes(earningRuleType)) {
        return ["reward_quantity_key"]
      }
      return [
        "reward_quantity_key",
        ...(rewardType === EarningRuleRewardType.coupons ? [] : ["reward_tpe_key"]),
      ]
    }
    let displayNormalSectionComponents = [];
    switch (earningRuleType) {
      case EarningRuleType.generalPurchase:
        const lineItemGroupsSection = tierTypeConfig.lineItemGroups
          ? [
            "line_item_requirement_key",
            ...(lineItemRequirement ? ["sku_type_selector_key"] : []),
          ]
          : [];
        displayNormalSectionComponents = [
          "per_transaction_key",
          "eligible_days_key",
          "upload_eligible_days_key",
          "is_exclude_delivery_cost_key",
          "is_exclude_other_charges_key",
          ...lineItemGroupsSection,
          "have_eligible_stores_key",
          ...(haveEligibleStores ? ["store_selector_key"] : []),
          "reward_logic_section_key",
          "reward_amount_input_area_key",
          "execution_priority_input_key",
          "have_limits_key",
          ...(haveLimits ? ["limits_split_line_key", "purchase_earning_rule_reward_limit_key"] : []),
        ]
        return {
          displayNormalSectionComponents,
          displayLanguageSectionComponents: [],
        }
      case EarningRuleType.qrCodeScanning:
        return {
          displayNormalSectionComponents: [
            "qr_code_scanning_section_key",
            ...(getRewardSectionDisplayComponents()),
            "have_limits_key",
            ...(haveLimits ? ["limits_split_line_key", "limit_section_key"] : []),
          ],
          displayLanguageSectionComponents: [],
        }
      case EarningRuleType.gpsCheckIn:
        return {
          displayNormalSectionComponents: [
            "gps_latitude_key",
            "gps_longitude_key",
            "gps_radius_key",
            ...(getRewardSectionDisplayComponents()),
            "have_limits_key",
            ...(haveLimits ? ["limits_split_line_key", "limit_section_key"] : []),
          ],
          displayLanguageSectionComponents: [],
        }
      case EarningRuleType.newMember:
        return {
          displayNormalSectionComponents: [
            "promotion_code_switch_key",
            ...(requirePromotionCode ? ["promotion_code_input_key", "promotion_code_max_times_key"] : []),
            "execution_priority_input_key",
            ...getRewardSectionDisplayComponents()
          ],
          displayLanguageSectionComponents: [],
        }
      case EarningRuleType.memberReferral:
        return {
          displayNormalSectionComponents: [
            "referral_type_key",
            ...(getRewardSectionDisplayComponents()),
            "execution_priority_input_key",
            ...(referralType !== 'INVITEE' ? ["have_limits_key"] : []),
            ...(referralType !== 'INVITEE' && haveLimits ? ["limits_split_line_key", "max_invitees_key"] : []),
          ],
          displayLanguageSectionComponents: [],
        }
      case EarningRuleType.birthday:
        return {
          displayNormalSectionComponents: [
            ...(getRewardSectionDisplayComponents()),
            "execution_priority_input_key",
          ],
          displayLanguageSectionComponents: [],
        }
      case EarningRuleType.levelUpgrade:
        return {
          displayNormalSectionComponents: [
            "level_upgrade_section_key",
            ...(getRewardSectionDisplayComponents()),
            "execution_priority_input_key",
            "have_limits_key",
            ...(haveLimits ? ["limits_split_line_key", "level_upgrade_per_head_periodic_limit_section_key"] : []),
          ],
          displayLanguageSectionComponents: [],
        }
      case EarningRuleType.fillingForm:
        return {
          displayNormalSectionComponents: [
            ...(getRewardSectionDisplayComponents()),
            "execution_priority_input_key",
            "have_limits_key",
            ...(haveLimits ? ["limits_split_line_key", "filling_form_section_key"] : []),
          ],
          displayLanguageSectionComponents: [],
        }
      default:
        return {
          displayNormalSectionComponents: [],
          displayLanguageSectionComponents: [],
        }
    }
  },
  languageSections: [],
  normalSections: {
    [EarningRuleType.generalPurchase]: [
      {
        sectionKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionKey,
        sectionTitle: "REQUIREMENTS",
        description: "The transaction must meet all the requirements set below to earn reward. ",
        descriptionClass: "tips-message",
        sectionContent: [
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.per_transaction_key,
            component: GeneralPurchasePerMin,
            componentProps: {
              containerClassName: "general-purchase-per-transaction-container",
              minSpendingTitle: "Minimum spending per transaction (optional)",
              minSpendingName: `${fieldPrefix}minSpending`,
              minQuantityTitle: "Minimum quantity per transaction (optional)",
              minQuantityName: `${fieldPrefix}minQuantity`,
            }
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.eligible_days_key,
            component: InputFieldControl,
            componentProps: {
              name: `${fieldPrefix}eligibleDays`,
              useDefaultFieldName: true,
              title: "Accept receipt X days after purchase for system handling (optional)",
              tips: "If purchase date is X days before today, system will not check earning rules and issue rewards.",
              type: "number",
              unit: "days",
              className: "coupon-campaign-section-input",
            },
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.upload_eligible_days_key,
            component: InputFieldControl,
            componentProps: {
              name: `${fieldPrefix}uploadEligibleDays`,
              useDefaultFieldName: true,
              title: "Upload receipt X days after purchase (optional)",
              tips: "If customers should purchase and upload receipt on the same day, input 0 for X. If input 1, means accept upload receipt before 23:59 of the following day.",
              type: "number",
              unit: "days",
              className: "coupon-campaign-section-input",
            },
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.is_exclude_delivery_cost_key,
            component: ReactHookFormSwitch,
            componentProps: {
              valueFieldName: `${fieldPrefix}isExcludeDeliveryCost`,
              title: 'Exclude delivery cost (for online purchase)',
            },
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.is_exclude_other_charges_key,
            component: ReactHookFormSwitch,
            componentProps: {
              valueFieldName: `${fieldPrefix}isExcludeOtherCharges`,
              title: 'Exclude other charges (for online purchase)',
            },
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.line_item_requirement_key,
            component: ReactHookFormSwitch,
            componentProps: {
              title: "Line item requirement",
              valueFieldName: `${fieldPrefix}lineItemRequirement`,
            }
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.sku_type_selector_key,
            component: SkuTypeSelector,
            componentProps: {
              prefix: fieldPrefix
            },
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.have_eligible_stores_key,
            component: ReactHookFormSwitch,
            componentProps: {
              valueFieldName: `${fieldPrefix}haveEligibleStores`,
              title: 'Eligible stores',
            },
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.store_selector_key,
            component: StoreMultipleSelector,
            componentProps: { valueFieldName: `${fieldPrefix}selectedStores` }
          },
        ]
      },
      {
        sectionKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.reward.sectionKey,
        sectionTitle: "REWARD",
        sectionContent: [
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.reward.sectionContent.reward_logic_section_key,
            component: RewardLogicSelector,
            componentProps: {
              prefix: fieldPrefix,
            },
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.reward.sectionContent.reward_amount_input_area_key,
            component: RewardAmountInputArea,
            componentProps: {
              prefix: fieldPrefix,
            },
          },
        ]
      },
      getExecutionPrioritySection(fieldPrefix),
      {
        sectionKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionKey,
        sectionContent: [
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.have_limits_key,
            component: ReactHookFormSwitch,
            componentProps: {
              title: "LIMIT",
              customTitleClass: "earning-rule-limit-section-title",
              valueFieldName: `${fieldPrefix}haveLimitsField`,
            }
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.limits_split_line_key,
            component: LimitsSplitLine,
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.purchase_earning_rule_reward_limit_key,
            component: PurchaseEarningRuleRewardLimitInputs,
            componentProps: {
              prefix: fieldPrefix,
            }
          },
        ]
      },
    ],
    [EarningRuleType.qrCodeScanning]: [
      {
        sectionKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionKey,
        sectionTitle: "REQUIREMENTS",
        description: "Customers can earn reward after scanning any one of the inputted / imported QR codes.",
        descriptionClass: "tips-message",
        sectionContent: [
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.qr_code_scanning_section_key,
            component: QRCodeScanningSection,
            componentProps: {
              prefix: fieldPrefix,
            },
          },
        ]
      },
      getRewardSection(fieldPrefix),
      {
        sectionKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionKey,
        sectionContent: [
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.have_limits_key,
            component: ReactHookFormSwitch,
            componentProps: {
              title: "LIMIT",
              customTitleClass: "earning-rule-limit-section-title",
              valueFieldName: `${fieldPrefix}haveLimitsField`,
            }
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.limits_split_line_key,
            component: LimitsSplitLine,
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.limit_section_key,
            component: LimitSection,
            componentProps: {
              fieldPrefix,
              tips: {
                'overallLimit': 'Maximum number of times to scan QR code check-in per campaign.',
                'periodicLimit': 'Maximum number of times to scan QR code check-in per period.',
                'perHeadLimit': 'Maximum number of times to scan QR code check-in per customer.',
                'perHeadPeriodicLimit': 'Maximum number of times to scan QR code check-in per customer per period.'
              }
            }
          },
        ]
      },
    ],
    [EarningRuleType.gpsCheckIn]: [
      {
        sectionKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionKey,
        sectionTitle: "REQUIREMENTS",
        description: "Set the location area where customers can check in and earn rewards.",
        descriptionClass: "tips-message",
        sectionContent: [
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.gps_latitude_key,
            component: InputFieldControl,
            componentProps: {
              name: `${fieldPrefix}latitude`,
              title: 'Latitude',
              useDefaultFieldName: true,
              type: 'number',
            }
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.gps_longitude_key,
            component: InputFieldControl,
            componentProps: {
              name: `${fieldPrefix}longitude`,
              title: 'Longitude',
              useDefaultFieldName: true,
              type: 'number',
            }
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.gps_radius_key,
            component: InputFieldControl,
            componentProps: {
              name: `${fieldPrefix}radius`,
              title: 'Radius',
              useDefaultFieldName: true,
              type: 'number',
              unit: "meters",
              className: 'coupon-campaign-section-input'
            }
          },
        ]
      },
      getRewardSection(fieldPrefix),
      {
        sectionKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionKey,
        sectionContent: [
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.have_limits_key,
            component: ReactHookFormSwitch,
            componentProps: {
              title: "LIMIT",
              customTitleClass: "earning-rule-limit-section-title",
              valueFieldName: `${fieldPrefix}haveLimitsField`,
            }
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.limits_split_line_key,
            component: LimitsSplitLine,
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.limit_section_key,
            component: LimitSection,
            componentProps: {
              fieldPrefix,
              tips: {
                'overallLimit': 'Maximum number of times to do GPS check-in per campaign.',
                'periodicLimit': 'Maximum number of times to do GPS check-in per period.',
                'perHeadLimit': 'Maximum number of times to do GPS check-in per customer.',
                'perHeadPeriodicLimit': 'Maximum number of times to do GPS check-in per customer per period.'
              }
            }
          },
        ]
      },
    ],
    [EarningRuleType.newMember]: [
      {
        sectionKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.promotionCode.sectionKey,
        sectionTitle: "PROMOTION CODE",
        sectionContent: [
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.promotionCode.sectionContent.promotion_code_switch_key,
            component: ReactHookFormSwitch,
            componentProps: {
              title: "Require promotion code",
              tips: "Require customer to input the correct “Promotion code” when sign up in order to earn reward.",
              valueFieldName: `${fieldPrefix}requirePromotionCode`,
            }
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.promotionCode.sectionContent.promotion_code_input_key,
            component: ReactHookFormTextInput,
            componentProps: {
              customClass: "width-400px",
              valueFieldName: `${fieldPrefix}promotionCode`,
              title: 'Promotion code',
            }
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.promotionCode.sectionContent.promotion_code_max_times_key,
            component: ReactHookFormTextInput,
            componentProps: {
              customClass: "width-160px",
              valueFieldName: `${fieldPrefix}promotionCodeUsageMaxLimit`,
              title: 'Maximum number of time this code can be used (optional)',
              unit: "times",
              type: "number",
            }
          },
        ],
      },
      getRewardSection(fieldPrefix),
      getExecutionPrioritySection(fieldPrefix),
    ],
    [EarningRuleType.memberReferral]: [
      {
        sectionKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.reward.sectionKey,
        sectionTitle: "REWARD",
        sectionContent: [
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.reward.sectionContent.referral_type_key,
            component: ReactHookFormTitleWithRadio,
            componentProps: {
              title: "Beneficiary",
              valueFieldName: `${fieldPrefix}referralType`,
              defaultValue: "INVITER",
              options: [
                { label: 'Inviter', value: 'INVITER' },
                { label: 'Invitee', value: 'INVITEE' },
                { label: 'Both', value: 'BOTH_INVITER_AND_INVITEE' },
              ]
            }
          },
          getRewardQuantityInput(fieldPrefix),
          getRewardTpeInput(fieldPrefix),
        ]
      },
      getExecutionPrioritySection(fieldPrefix),
      {
        sectionKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionKey,
        sectionContent: [
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.have_limits_key,
            component: ReactHookFormSwitch,
            componentProps: {
              title: "LIMIT",
              customTitleClass: "earning-rule-limit-section-title",
              valueFieldName: `${fieldPrefix}haveLimitsField`,
            }
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.limits_split_line_key,
            component: LimitsSplitLine,
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.max_invitees_key,
            component: InputFieldControl,
            componentProps: {
              name: `${fieldPrefix}maxInvitees`,
              title: 'Maximum number of invitees (optional, for inviters only)',
              useDefaultFieldName: true,
              type: 'number',
            }
          },
        ]
      }
    ],
    [EarningRuleType.birthday]: [
      getRewardSection(fieldPrefix),
      getExecutionPrioritySection(fieldPrefix),
    ],
    [EarningRuleType.levelUpgrade]: [
      {
        sectionKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionKey,
        sectionTitle: "REQUIREMENTS",
        description: "Set which upgrade condition can earn reward.",
        descriptionClass: "tips-message",
        sectionContent: [
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.level_upgrade_section_key,
            component: LevelUpgradeSection,
            componentProps: {
              prefix: fieldPrefix,
            },
          },
        ]
      },
      getRewardSection(fieldPrefix),
      getExecutionPrioritySection(fieldPrefix),
      {
        sectionKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionKey,
        sectionContent: [
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.have_limits_key,
            component: ReactHookFormSwitch,
            componentProps: {
              title: "LIMIT",
              customTitleClass: "earning-rule-limit-section-title",
              valueFieldName: `${fieldPrefix}haveLimitsField`,
            }
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.limits_split_line_key,
            component: LimitsSplitLine,
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.level_upgrade_per_head_periodic_limit_section_key,
            component: LevelUpgradePerHeadPeriodicLimitSection,
            componentProps: {
              perHeadPeriodicLimitFieldName: `${fieldPrefix}perHeadPeriodicLimit`,
              perHeadPeriodicLimitDaysFieldName:`${fieldPrefix}perHeadPeriodicLimitDays`,
            }
          },
        ]
      },
    ],
    [EarningRuleType.fillingForm]: [
      getRewardSection(fieldPrefix),
      getExecutionPrioritySection(fieldPrefix),
      {
        sectionKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionKey,
        sectionContent: [
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.have_limits_key,
            component: ReactHookFormSwitch,
            componentProps: {
              title: "LIMIT",
              customTitleClass: "earning-rule-limit-section-title",
              valueFieldName: `${fieldPrefix}haveLimitsField`,
            }
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.limits_split_line_key,
            component: LimitsSplitLine,
          },
          {
            key: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.filling_form_section_key,
            component: LimitSection,
            componentProps: {
              fieldPrefix,
              tips: {
                overallLimit: 'Maximum number of times to fill form per campaign.',
                periodicLimit: 'Maximum number of times to fill form per period.',
                perHeadLimit: 'Maximum number of times to fill form per customer.',
                perHeadPeriodicLimit: 'Maximum number of times to fill form per customer per period.'
              }
            }
          },
        ]
      },
    ],
  },
})

export const CREATE_CAMPAIGN_EARNING_PAGE_CONFIG = getEarningRuleCreationConfig("linkedEarningRules.") 
